import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import dayjs from 'dayjs';

/**
 * Export data to a proper .xlsx file.
 * @param {string} filename - base filename without extension
 * @param {string} sheetName - worksheet tab name
 * @param {string[]} headers - column header labels
 * @param {Array<Array<any>>} rows - array of row arrays
 * @param {object} [meta] - optional { title, subtitle } shown above the table
 */
export const exportToExcel = (filename, sheetName, headers, rows, meta = {}) => {
    const wb = XLSX.utils.book_new();

    const wsData = [];

    if (meta.title) wsData.push([meta.title]);
    if (meta.subtitle) wsData.push([meta.subtitle]);
    if (meta.title || meta.subtitle) wsData.push([]);

    wsData.push(headers);
    rows.forEach(r => wsData.push(r));

    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Auto-width columns
    const colWidths = headers.map((h, i) => {
        const maxLen = Math.max(
            String(h).length,
            ...rows.map(r => String(r[i] ?? '').length)
        );
        return { wch: Math.min(maxLen + 4, 50) };
    });
    ws['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, `${filename}-${dayjs().format('YYYY-MM-DD-HHmm')}.xlsx`);
};

/**
 * Export data to a .csv file.
 */
export const exportToCsv = (filename, headers, rows) => {
    const escapeCell = (val) => {
        const str = String(val ?? '');
        return str.includes(',') || str.includes('"') || str.includes('\n')
            ? `"${str.replace(/"/g, '""')}"`
            : str;
    };

    const csv = [headers, ...rows]
        .map(row => row.map(escapeCell).join(','))
        .join('\n');

    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}-${dayjs().format('YYYY-MM-DD-HHmm')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

/**
 * Export data to a .pdf file using jsPDF autoTable.
 * @param {string} filename - base filename without extension
 * @param {string} title - report title shown at top
 * @param {string[]} headers - column header labels
 * @param {Array<Array<any>>} rows - array of row arrays
 * @param {object} [meta] - optional { subtitle, orientation: 'portrait'|'landscape' }
 */
export const exportToPdf = (filename, title, headers, rows, meta = {}) => {
    const orientation = meta.orientation || (headers.length > 6 ? 'landscape' : 'portrait');
    const doc = new jsPDF({ orientation, unit: 'mm', format: 'a4' });

    const pageW = doc.internal.pageSize.getWidth();

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(title, pageW / 2, 16, { align: 'center' });

    if (meta.subtitle) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(meta.subtitle, pageW / 2, 23, { align: 'center' });
    }

    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(`Generated: ${dayjs().format('YYYY-MM-DD HH:mm')}`, pageW / 2, meta.subtitle ? 29 : 23, { align: 'center' });
    doc.setTextColor(0);

    autoTable(doc, {
        head: [headers],
        body: rows.map(r => r.map(v => String(v ?? ''))),
        startY: meta.subtitle ? 34 : 28,
        styles: { fontSize: 8, cellPadding: 3 },
        headStyles: { fillColor: [22, 119, 255], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 248, 255] },
        margin: { left: 10, right: 10 },
    });

    doc.save(`${filename}-${dayjs().format('YYYY-MM-DD-HHmm')}.pdf`);
};
